import {addToQuery, isComplexQuery, replaceInQuery} from '@github-ui/issue-url-helper'

import {tokenizeQuery} from './query-tokenizer'

function addFilterToQuery(query: string | undefined, prefix: string, value: string): string {
  const filter = `${prefix}:${value}`

  if (!query) return filter
  // Removing terms by prefix would also drop attached parentheses and leave dangling
  // operators in grouped queries, so append without replacing the existing filter
  if (isComplexQuery(query)) return `${query} ${filter}`

  const filterTerms = tokenizeQuery(query)
    .filter(token => token.kind === 'term')
    .map(token => token.value)
  const nonMatchingTerms = filterTerms.filter(term => !term.startsWith(`${prefix}:`))
  if (nonMatchingTerms.length === 0) return filter

  const newQuery = filterTerms.length === nonMatchingTerms.length ? query : nonMatchingTerms.join(' ')
  return `${newQuery} ${filter}`
}

export function addRepoFilterToQuery(query: string | undefined, repo: string): string {
  return addFilterToQuery(query, 'repo', repo)
}

export function addAuthorFilterToQuery(query: string | undefined, author: string): string {
  return addFilterToQuery(query, 'author', author)
}

export function addLabelFilterToQuery(query: string | undefined, label: string): string {
  const labelValue = label.includes(' ') ? `"${label}"` : label
  return addFilterToQuery(query, 'label', labelValue)
}

export function addAssigneeFilterToQuery(query: string | undefined, assignee: string): string {
  return addFilterToQuery(query, 'assignee', assignee)
}

export function addTypeFilterToQuery(query: string | undefined, type: string): string {
  const typeValue = type.includes(' ') ? `"${type}"` : type
  return addFilterToQuery(query, 'type', typeValue)
}

export function addMilestoneFilterToQuery(query: string | undefined, milestone: string): string {
  const milestoneValue = milestone.includes(' ') ? `"${milestone}"` : milestone
  return addFilterToQuery(query, 'milestone', milestoneValue)
}

/** Replaces any `created:`, `updated:` or `closed:` filter with that one day, as `YYYY-MM-DD`. */
export function addDateFilterToQuery(
  query: string | undefined,
  qualifier: 'created' | 'updated' | 'closed',
  date: string,
): string {
  return addFilterToQuery(query, qualifier, date)
}

export function addBlockedFilterToQuery(query: string | undefined): string {
  // `is:` is shared with `is:issue`/`is:open`, so this can't use the prefix-based
  // `addFilterToQuery`. `addToQuery` matches the exact `is:blocked` term instead
  if (!query) return 'is:blocked'
  return addToQuery(query, 'is', 'blocked')
}

export function addParentIssueFilterToQuery(
  query: string | undefined,
  repoNameWithOwner: string,
  number: number,
): string {
  const parentIssue = `${repoNameWithOwner}#${number}`
  const filter = `parent-issue:${parentIssue}`
  if (!query) return filter

  // The whitespace tokenizer in `addFilterToQuery` drops whole tokens by prefix,
  // so it would strip the trailing `)` from `(is:issue OR parent-issue:o/r#1)`
  // and leave a dangling `OR AND`, isComplexQuery check for those cases and use
  // the additive fallback instead of replacing the existing parent-issue filter.
  if (isComplexQuery(query)) return addToQuery(query, 'parent-issue', parentIssue)
  return replaceInQuery(query, 'parent-issue', parentIssue) ?? filter
}
